/***************************************************************************************************
** buildResourceDetailPageActionMenuItems
***************************************************************************************************/

import { pascalCase } from "change-case";

import { interpolate, evaluateConditional } from "@utils";
import buildInterpolationData from "@utils/buildInterpolationData";
import { buildFavoritedPageActionMenuItem } from "@utils/favoritedPages";

const DIVIDER_ACTION_MENU_ITEM = {
  type: "divider",
};

const RELOAD_PAGE_ACTION_MENU_ITEM = {
  type: "reload_page",
};

const COPY_URL_ACTION_MENU_ITEM = {
  type: "clipboard_copy",
  label: {
    text: "Copy URL to Clipboard",
    icon: "link",
  },
  hotkeys: ["meta+shift+u", "ctrl+shift+u"],
  successMessage: "Copied <b>Record URL</b> to Clipboard!",
  valueFn: () => window.location.href,
};

const COPY_URL_QUERY_ACTION_MENU_ITEM = {
  ...COPY_URL_ACTION_MENU_ITEM,
  hotkeys: ["meta+shift+c", "ctrl+shift+c"],
};

const COPY_ID_ACTION_MENU_ITEM = {
  type: "clipboard_copy",
  label: {
    text: "Copy ID to Clipboard",
    icon: "binary",
  },
  value: "%{id}",
  hotkeys: ["meta+shift+i", "ctrl+shift+i"],
  successMessage: "Copied <b>Record ID</b> to Clipboard!",
};

const buildCreateRelatedTaskItem = (record = {}) => {
  if (!record) return [];
  if (!record.id) return [];
  if (!record.resource_name) return [];

  return [
    {
      type: "modal",
      label: {
        text: "Create Related Task",
        icon: "hexagon-check",
      },
      hotkeys: ["alt+t"],
      modalConfigPath: "resources/tasks/_modal",
      modalConfig: {
        title: "Create Related Task",
        callbackAction: null,
      },
      modalData: {
        related_record_id: record.id,
        related_record_type: pascalCase(record.resource_name),
        related_record_description: record.description,
      },
    },
    DIVIDER_ACTION_MENU_ITEM,
  ];
};

export const buildStandardActionMenuItems = ({
  path,
  pageKey,
  defaultLabel,
  defaultIcon,
  resourceName,
  includeCopyId = true,
}) => {
  const favoritedPageActionMenuItem = buildFavoritedPageActionMenuItem({
    path: path,
    pageKey: pageKey,
    defaultLabel: defaultLabel,
    defaultIcon: defaultIcon,
    resourceName: resourceName,
  });

  return [
    ...(favoritedPageActionMenuItem ? [favoritedPageActionMenuItem] : []),
    RELOAD_PAGE_ACTION_MENU_ITEM,
    includeCopyId ? COPY_URL_ACTION_MENU_ITEM : COPY_URL_QUERY_ACTION_MENU_ITEM,
    ...(includeCopyId ? [COPY_ID_ACTION_MENU_ITEM] : []),
  ];
};

export default function buildResourceDetailPageActionMenuItems({
  itemsFromConfig = [],
  recordDeletion,
  recordDataPath,
  kinndom = {},
  record = {},
}) {
  const interpolationData = buildInterpolationData({
    kinndom: kinndom,
    record: record,
  });

  const items = [
    ...(itemsFromConfig?.length > 0 ? [...itemsFromConfig, DIVIDER_ACTION_MENU_ITEM] : []),
    ...(buildCreateRelatedTaskItem(record)),
    ...buildStandardActionMenuItems({
      path: recordDataPath,
      defaultLabel: record.description,
      resourceName: record.resource_name,
    }),
  ];

  if (recordDeletion?.enabled && recordDataPath) {
    const [resourceSlug, recordId] = recordDataPath.replace(/^\//, "").split("/");

    const recordDeletionItem = {
      type: "record_deletion",
      record: {
        type: resourceSlug,
        id: recordId,
      },
    };

    if (recordDeletion.disabledText) {
      recordDeletionItem.disabledText = recordDeletion.disabledText;
    }

    if (recordDeletion.enabled !== true) {
      recordDeletionItem.isEnabled = recordDeletion.enabled;
    }

    items.push(DIVIDER_ACTION_MENU_ITEM, recordDeletionItem);
  }

  // Interpolate items
  const interpolatedItems = interpolate(items, interpolationData, {
    fallbackValue: "",
  });

  // Process item enabled/disabled state
  return interpolatedItems.map((item) => {
    const { isEnabled, isDisabled, ...rest } = item;

    if (isDisabled !== undefined) {
      return {
        ...rest,
        isDisabled: evaluateConditional(isDisabled, interpolationData),
      };
    } else if (isEnabled !== undefined) {
      return {
        ...rest,
        isDisabled: !evaluateConditional(isEnabled, interpolationData),
      };
    }

    return item;
  });
}
